import { useEffect } from "react"

import { Form, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import BusinessInfoForm from "../../../Components/Forms/BusinessInfoForm"
import { usePhoneCountries } from "../../../hooks/usePhoneCountries"
import { bindPendingProviderBanner, setProviderBanner } from "../../../Utils/anotherProviderBanner"
import { initialPhoneValue } from "../../../Utils/phone/readPhone"
import { createAccountStepPath, onboardingStepPath } from "../../../Utils/utils"
import { authErrorReset } from "../../LoginPage/redux/auth.actions"
import { userActions } from "../../User/redux/user.slice"
import {
  createBusinessAccount,
  getUserInfo,
  trackEvent,
  updateBusinessAccount
} from "../../User/redux/user.thunks"
import { updateCompletedStep } from "../../User/services"

import "./styles.less"

const OnboardingBusinessInfoPage = (props) => {
  const { step } = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { countries = [], loading } = usePhoneCountries()

  const { error, fetching, userInfo, isAdmin } = useSelector((state) => state.auth)
  const businessAccountsFetching = useSelector((state) => state.user.businessAccountsFetching)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const nextCreateAccountStep = useSelector((state) => state.user.nextCreateAccountStep)
  const saveResult = useSelector((state) => state.user.saveResult)
  const isOnboarding = pathname.includes("/onboarding")
  const isCreateBusinessInfoStep = pathname.includes("/create-account/business-info")

  const getPhoneData = () =>
    initialPhoneValue(countries, userInfo?.phone, userInfo?.phone_code?.country_code)

  const getInitialBusinessValues = () => {
    const shouldShowEmptyValues =
      !businessAccount || (isCreateBusinessInfoStep && nextCreateAccountStep === 1)

    if (shouldShowEmptyValues) {
      return {
        name: "",
        url: "",
        is_use_another_solutions: false,
        another_solution_name: "",
        previously_connected_enrollments: [],
        is_user_notified_previous_solution: false,
        phone: getPhoneData()
      }
    }

    return {
      name: businessAccount?.name || "",
      url: businessAccount?.url || "",
      is_use_another_solutions: businessAccount?.is_use_another_solutions || false,
      another_solution_name: businessAccount?.another_solution_name || "",
      previously_connected_enrollments: businessAccount?.previously_connected_enrollments || [],
      is_user_notified_previous_solution:
        businessAccount?.is_user_notified_previous_solution || false,
      phone: getPhoneData()
    }
  }

  function handleSubmit(data) {
    const { phone, ...rest } = data
    const fullData = {
      ...rest,
      country_name: phone?.name,
      phone_code_id: phone?.id
    }
    // An incomplete number is left out of the save: the server's PhoneNumberField
    // would reject it and fail the whole form. An empty field clears the phone.
    if (phone?.valid) fullData.phone = phone.e164
    else if (!phone?.number?.trim()) fullData.phone = ""

    if (isOnboarding) {
      setProviderBanner(businessAccount?.id, data.is_use_another_solutions)
    }

    if (businessAccount && nextCreateAccountStep !== 1) {
      dispatch(
        updateBusinessAccount({
          id: businessAccount?.id,
          ...fullData
        })
      )
    } else {
      dispatch(createBusinessAccount(fullData))
    }
  }

  useEffect(() => {
    if (userInfo && !isAdmin) {
      form.resetFields()
      form.setFieldsValue(getInitialBusinessValues())
    }

    dispatch(getUserInfo())
    document.title = "Business Info - Chargeback"
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [businessAccount, countries.length, nextCreateAccountStep, pathname])

  useEffect(() => {
    if (error) dispatch(authErrorReset())
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [error])

  useEffect(() => {
    if (saveResult && !saveResult?.confirm) {
      if (userInfo?.new_onboarding_finished) {
        if (nextCreateAccountStep && businessAccount?.id) {
          bindPendingProviderBanner(businessAccount.id)
          updateCompletedStep({
            step,
            business_account_id: businessAccount?.id
          })
          navigate(createAccountStepPath[step + 1])
        }
      } else {
        bindPendingProviderBanner(businessAccount?.id || userInfo?.current_business_account_id)
        navigate(onboardingStepPath[step + 2 + 1])
      }

      dispatch(userActions.userSaveResultReset())
    }
  }, [saveResult])

  useEffect(() => {
    dispatch(trackEvent({ event_type: "first_step" }))
  }, [])

  return (
    <div className="ob-business">
      <Spin size="large" spinning={fetching || businessAccountsFetching}>
        <div className="ob-business__row">
          <div className="ob-business__content">
            <div className="ob-business__main ob-business__main--form">
              <div className="ob-business__card">
                <h2 className="ob-business__title">{t("Business information")}</h2>

                <h4 className="ob-business__subtitle">
                  {t("Let’s set up your Chargeback account.")}
                </h4>

                <BusinessInfoForm
                  form={form}
                  onSubmit={handleSubmit}
                  onboarding={!userInfo?.new_onboarding_finished}
                  disabled={loading && !countries.length}
                />
              </div>
            </div>
          </div>
        </div>
      </Spin>
    </div>
  )
}

export default OnboardingBusinessInfoPage
